import {
  CSSProperties, MouseEvent, ReactNode, useState,
} from 'react';
import { useTranslation } from 'react-i18next';

/** The id every page's single <main> carries (PageMain below). */
export const MAIN_ID = 'main';

/**
 * A page's one <main>: the landmark (QA 2026-09-25 #29) and the skip link's
 * target. tabIndex -1 lets the link put focus on it without adding a tab
 * stop; it is a landmark, not a control, so it draws no focus ring.
 * `<main>` and the `<div>` it replaces are both plain blocks under the Sass
 * reset, so the page lays out exactly as before.
 */
export function PageMain({
  className,
  children,
  testId,
  maskReplay,
}: {
  className?: string;
  children?: ReactNode;
  testId?: string;
  /** Mask this page's text in session replays (personal data on it). */
  maskReplay?: boolean;
}) {
  return (
    <main
      id={MAIN_ID}
      tabIndex={-1}
      className={className}
      style={{ outline: 'none' }}
      data-testid={testId}
      data-mp-mask={maskReplay ? '' : undefined}
    >
      {children}
    </main>
  );
}

/**
 * "Skip to main content" (QA 2026-09-25 #29, WCAG 2.4.1): the first tab stop
 * on every page, for every tenant, jumping past the header to the page's
 * <main id="main">. Off-screen until it has focus.
 *
 * Styled inline, on purpose: a rule in client/src/sass would change the
 * bundled stylesheet every tenant downloads (and Go Oil's build hash), and
 * public/aperio/theme.css only loads for the Aperio preset. Colours come
 * from the theme variables both tenants already set, so it wears each
 * tenant's own navy/dark primary.
 */
export default function SkipLink() {
  const { t } = useTranslation();
  const [focused, setFocused] = useState(false);

  const onClick = (event: MouseEvent<HTMLAnchorElement>) => {
    const main = document.getElementById(MAIN_ID);
    if (!main) return;
    // Move focus as well as the viewport: a bare hash jump leaves focus on
    // the link in some browsers, and a SPA must not touch the URL.
    event.preventDefault();
    main.focus({ preventScroll: true });
    main.scrollIntoView({ block: 'start' });
  };

  const style: CSSProperties = {
    position: 'fixed',
    left: 12,
    top: focused ? 12 : -80,
    zIndex: 1000,
    padding: '12px 20px',
    borderRadius: 6,
    background: 'var(--color-primary-darkest, #0b2b3c)',
    color: '#ffffff',
    fontSize: 14,
    fontWeight: 600,
    lineHeight: 1.2,
    textDecoration: 'none',
    whiteSpace: 'nowrap',
    // 2px ring, 2px off the chip, so both its neighbours are the page's
    // light background: the dark ring is well over 3:1 there.
    outline: focused
      ? '2px solid var(--color-primary-darkest, #0b2b3c)'
      : undefined,
    outlineOffset: 2,
  };

  return (
    <a
      className="skip-link"
      href={`#${MAIN_ID}`}
      style={style}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
      onClick={onClick}
    >
      {t('a11y.skip')}
    </a>
  );
}
